import { request } from '@owl-nest/api-client'
import { type BaseAddress } from '@owl-nest/api-client/latest'
import * as geolocation from '@owl-nest/geolocation'
import * as logger from '@owl-nest/logger'
import * as env from '@owl-nest/config'

type LatLong = {
  latitude: number
  longitude: number
}

export type Result = { address: BaseAddress & LatLong; highlight: BaseAddress }

type FeatureCollection = {
  features: Feature[]
}

type Feature = {
  address?: string
  context: ContextFeature[]
  id: string
  text: string
  geometry: {
    coordinates: [number, number]
  }
}

type ContextFeature = {
  id: string
  text: string
  short_code?: string
}

async function getMapboxUrl(
  query: string,
  {
    countries,
    language,
    types = ['address'],
  }: {
    countries: string[]
    language: string
    types?: (
      | 'address'
      | 'country'
      | 'district'
      | 'locality'
      | 'neighborhood'
      | 'place'
      | 'poi'
      | 'postcode'
      | 'region'
    )[]
  },
): Promise<URL> {
  const url = new URL(`/geocoding/v5/mapbox.places/${query}.json`, 'https://api.mapbox.com')

  url.searchParams.set('autocomplete', 'true')
  if (env.MAPBOX_API_KEY) {
    url.searchParams.set('access_token', env.MAPBOX_API_KEY)
  } else {
    logger.err('[mapbox] Missing `access_token`')
  }
  url.searchParams.set('language', language)
  if (countries.length !== 0) {
    url.searchParams.set('country', countries.join(','))
  }
  url.searchParams.set('fuzzyMatch', 'true')
  url.searchParams.set('limit', '10')
  url.searchParams.set('routing', 'false')
  url.searchParams.set('types', types.join(','))

  const fix = await geolocation.getFix()
  fix.next((response) => {
    url.searchParams.set('proximity', `${response.longitude},${response.latitude}`)
  })

  return url
}

export async function find(query: string, language: string, countries: string[] = []): Promise<Result[]> {
  const url = await getMapboxUrl(query, { language, countries })

  const response = await request<FeatureCollection>(url.toString(), {
    method: 'GET',
  })

  return response.caseOf({
    right: ({ body }) => {
      return body.features.map(feature2Result).filter((result): result is Result => result !== undefined)
    },
    left: (_) => {
      return []
    },
  })
}

export async function findPlace(
  query: string,
  language: string,
  countries: string[] = [],
): Promise<{ city: string; country: string; countryCode: string }[]> {
  const url = await getMapboxUrl(query, { countries, language, types: ['place'] })

  const response = await request<FeatureCollection>(url.toString(), {
    method: 'GET',
  })

  return response.caseOf({
    right: ({ body }) => {
      return body.features
        .map(feature2Place)
        .filter((result): result is { city: string; country: string; countryCode: string } => result !== undefined)
    },
    left: (_) => {
      return []
    },
  })
}

function feature2Place(feature: Feature): { city: string; country: string; countryCode: string } | undefined {
  const context = formatContext(feature)

  try {
    return {
      city: feature.text,
      country: context.country?.text ?? '',
      countryCode: context.country?.short_code ?? '',
    }
  } catch {
    return undefined
  }
}

function feature2Result(feature: Feature): Result | undefined {
  const context = formatContext(feature)

  try {
    return {
      address: {
        address1: feature.address ? `${feature.address} ${feature.text}` : feature.text,
        address2: '',
        city: context.place?.text ?? '',
        country: getCountry(context.country),
        postal_code: context.postcode?.text ?? '',
        state: context.region?.text ?? '',
        latitude: feature.geometry.coordinates[1],
        longitude: feature.geometry.coordinates[0],
      },
      highlight: {
        address1: feature.address ? `${feature.address} ${feature.text}` : feature.text,
        address2: '',
        city: context.locality ? context.locality.text : context.place ? context.place.text : '',
        country: getCountry(context.country),
        postal_code: context.postcode?.text ?? '',
        state: context.region?.text ?? '',
      },
    }
  } catch {
    return undefined
  }
}

type Context = {
  country?: ContextFeature
  region?: ContextFeature
  postcode?: ContextFeature
  place?: ContextFeature
  locality?: ContextFeature
}

function formatContext(feature: Feature): Context {
  const context: Context = {}
  for (const contextFeature of feature.context) {
    if (contextFeature.id.startsWith('locality')) {
      context.locality = contextFeature
    } else if (contextFeature.id.startsWith('place')) {
      context.place = contextFeature
    } else if (contextFeature.id.startsWith('postcode')) {
      context.postcode = contextFeature
    } else if (contextFeature.id.startsWith('region')) {
      context.region = contextFeature
    } else if (contextFeature.id.startsWith('country')) {
      context.country = contextFeature
    }
  }
  // Mapbox context data does not include country in some cases (Reunion island...)
  // See https://docs.mapbox.com/api/search/geocoding/#geocoding-response-object (section 'A note about multi-typing')
  if (!context.country) context.country = context.region
  return context
}

function getCountry(countryContext: ContextFeature | undefined): string {
  if (countryContext?.short_code) {
    // For Singapore, we get a region code (ex: SG-04)
    // See https://docs.mapbox.com/api/search/geocoding/#geocoding-response-object (section 'A note about multi-typing')
    if (countryContext.short_code?.startsWith('SG')) {
      return 'SG'
    }

    return countryContext.short_code
  }

  return ''
}
